// ============================================================================
// KLINISOL CRO - Personnel Profile Page
// File: src/pages/PersonnelProfile.js
// FDA 21 CFR Part 11 / ICH-GCP E6(R3) Compliant
// Version: 1.0
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    User,
    ArrowLeft,
    FileText,
    Award,
    Upload,
    Download,
    CheckCircle,
    XCircle,
    Clock,
    AlertTriangle,
    RefreshCw,
    X,
    Briefcase,
    Mail,
    Calendar,
    Shield,
    Eye,
    Plus,
    Edit,
    HelpCircle
} from 'lucide-react';
import personnelApi from '../api/personnelApi';
import { useAuth } from '../context/AuthContext';

const PersonnelProfile = () => {
    const navigate = useNavigate();
    const { userId } = useParams();
    const { user: currentUser } = useAuth();
    
    // State
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [accessLevel, setAccessLevel] = useState('self');
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showCredentialModal, setShowCredentialModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [activeTab, setActiveTab] = useState('documents');

    // Load profile data
    useEffect(() => {
        loadProfile();
    }, [userId]);

    const loadProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await personnelApi.getProfile(userId);
            setProfile(response.data);
            setAccessLevel(response.accessLevel || 'self');
        } catch (err) {
            console.error('Error loading profile:', err);
            setError(err.response?.data?.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    };

    // Download document
    const handleDownload = async (documentId, fileName) => {
        try {
            const blob = await personnelApi.downloadDocument(documentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading document:', err);
            alert('Failed to download document');
        }
    };

    // Approve/Reject document (admin only)
    const handleApproval = async (documentId, status) => {
        const reason = status === 'rejected' ? prompt('Enter rejection reason:') : null;
        if (status === 'rejected' && !reason) return;

        try {
            await personnelApi.approveDocument(documentId, status, reason);
            loadProfile(); // Refresh
        } catch (err) {
            console.error('Error updating approval:', err);
            alert('Failed to update document approval');
        }
    };

    // Upload Modal Component
    const UploadModal = () => {
        const [uploading, setUploading] = useState(false);
        const [file, setFile] = useState(null);
        const [formData, setFormData] = useState({
            document_type: 'CV',
            document_title: '',
            description: '',
            document_date: new Date().toISOString().split('T')[0],
            effective_date: '',
            expiration_date: '',
            version: '1.0'
        });

        const handleFileChange = (e) => {
            const selectedFile = e.target.files[0];
            if (selectedFile) {
                setFile(selectedFile);
                if (!formData.document_title) {
                    setFormData(prev => ({ ...prev, document_title: selectedFile.name }));
                }
            }
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!file) {
                alert('Please select a file');
                return;
            }

            setUploading(true);
            try {
                const data = new FormData();
                data.append('file', file);
                Object.keys(formData).forEach(key => {
                    if (formData[key]) data.append(key, formData[key]);
                });

                await personnelApi.uploadDocument(userId, data);
                setShowUploadModal(false);
                loadProfile(); // Refresh
            } catch (err) {
                console.error('Error uploading:', err);
                alert(err.response?.data?.message || 'Failed to upload document');
            } finally {
                setUploading(false);
            }
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white p-4 rounded-t-xl">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Upload className="w-5 h-5" />
                                <h2 className="text-lg font-bold">Upload Document</h2>
                            </div>
                            <button onClick={() => setShowUploadModal(false)} className="hover:bg-white/20 p-1 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {/* Document Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Document Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={formData.document_type}
                                onChange={(e) => setFormData(prev => ({ ...prev, document_type: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                required
                            >
                                {personnelApi.documentTypes.map(type => (
                                    <option key={type.value} value={type.value}>
                                        {type.label} {type.required && '*'}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* File Upload */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                File <span className="text-red-500">*</span>
                            </label>
                            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#E91E63] transition-colors">
                                <input
                                    type="file"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="file-upload"
                                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                />
                                <label htmlFor="file-upload" className="cursor-pointer">
                                    {file ? (
                                        <div className="flex items-center justify-center gap-2 text-green-600">
                                            <CheckCircle className="w-5 h-5" />
                                            <span>{file.name}</span>
                                        </div>
                                    ) : (
                                        <div className="text-gray-500">
                                            <Upload className="w-8 h-8 mx-auto mb-2" />
                                            <span>Click to select file</span>
                                            <p className="text-xs mt-1">PDF, DOC, DOCX, JPG, PNG</p>
                                        </div>
                                    )}
                                </label>
                            </div>
                        </div>

                        {/* Document Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Document Title</label>
                            <input
                                type="text"
                                value={formData.document_title}
                                onChange={(e) => setFormData(prev => ({ ...prev, document_title: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="Enter document title"
                            />
                        </div>

                        {/* Document Date */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Document Date</label>
                                <input
                                    type="date"
                                    value={formData.document_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, document_date: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Version</label>
                                <input
                                    type="text"
                                    value={formData.version}
                                    onChange={(e) => setFormData(prev => ({ ...prev, version: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                    placeholder="1.0"
                                />
                            </div>
                        </div>

                        {/* Expiration Date (for certificates) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Expiration Date <span className="text-gray-400">(if applicable)</span>
                            </label>
                            <input
                                type="date"
                                value={formData.expiration_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                rows={2}
                                placeholder="Optional description"
                            />
                        </div>

                        {/* Submit */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowUploadModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={uploading || !file}
                                className="flex-1 px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B] disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {uploading ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Uploading...
                                    </>
                                ) : (
                                    <>
                                        <Upload className="w-4 h-4" />
                                        Upload
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // Credential Modal Component
    const CredentialModal = () => {
        const [saving, setSaving] = useState(false);
        const [formData, setFormData] = useState({
            credential_type: 'GCP_CERTIFICATE',
            credential_name: '',
            credential_number: '',
            issuing_body: '',
            issue_date: new Date().toISOString().split('T')[0],
            expiration_date: '',
            document_id: ''
        });

        const handleSubmit = async (e) => {
            e.preventDefault();
            if (!formData.credential_name || !formData.issuing_body || !formData.issue_date) {
                alert('Please fill in required fields');
                return;
            }

            setSaving(true);
            try {
                await personnelApi.addCredential(userId, {
                    ...formData,
                    document_id: formData.document_id || null,
                    expiration_date: formData.expiration_date || null
                });
                setShowCredentialModal(false);
                loadProfile(); // Refresh
            } catch (err) {
                console.error('Error adding credential:', err);
                alert(err.response?.data?.message || 'Failed to add credential');
            } finally {
                setSaving(false);
            }
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white p-4 rounded-t-xl">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Award className="w-5 h-5" />
                                <h2 className="text-lg font-bold">Add Credential</h2>
                            </div>
                            <button onClick={() => setShowCredentialModal(false)} className="hover:bg-white/20 p-1 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {/* Credential Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Credential Type <span className="text-red-500">*</span>
                            </label>
                            <select
                                value={formData.credential_type}
                                onChange={(e) => setFormData(prev => ({ ...prev, credential_type: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                required
                            >
                                {personnelApi.credentialTypes.map(type => (
                                    <option key={type.value} value={type.value}>{type.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* Credential Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Credential Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.credential_name}
                                onChange={(e) => setFormData(prev => ({ ...prev, credential_name: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="e.g., CITI GCP Certificate, NY RN License"
                                required
                            />
                        </div>

                        {/* Credential Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Credential/License Number
                            </label>
                            <input
                                type="text"
                                value={formData.credential_number}
                                onChange={(e) => setFormData(prev => ({ ...prev, credential_number: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="License or certificate number"
                            />
                        </div>

                        {/* Issuing Body */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Issuing Body <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.issuing_body}
                                onChange={(e) => setFormData(prev => ({ ...prev, issuing_body: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="e.g., CITI Program, NY State Board of Nursing"
                                required
                            />
                        </div>

                        {/* Dates */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Issue Date <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="date"
                                    value={formData.issue_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, issue_date: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Expiration Date
                                </label>
                                <input
                                    type="date"
                                    value={formData.expiration_date}
                                    onChange={(e) => setFormData(prev => ({ ...prev, expiration_date: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                />
                                <p className="text-xs text-gray-500 mt-1">Leave blank if no expiration</p>
                            </div>
                        </div>

                        {/* Link to Document */}
                        {profile?.documents?.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Link to Uploaded Document
                                </label>
                                <select
                                    value={formData.document_id}
                                    onChange={(e) => setFormData(prev => ({ ...prev, document_id: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                >
                                    <option value="">-- None --</option>
                                    {profile.documents
                                        .filter(d => ['GCP_CERTIFICATE', 'PROFESSIONAL_LICENSE'].includes(d.document_type))
                                        .map(doc => (
                                            <option key={doc.id} value={doc.id}>
                                                {doc.document_title || doc.file_name}
                                            </option>
                                        ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">Optional: link to uploaded certificate file</p>
                            </div>
                        )}

                        {/* Submit */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowCredentialModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B] disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-4 h-4" />
                                        Add Credential
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // Edit Qualifications Modal Component
    const EditQualificationsModal = () => {
        const [saving, setSaving] = useState(false);
        const [formData, setFormData] = useState({
            department: profile?.qualifications?.department || '',
            job_title: profile?.qualifications?.job_title || '',
            hire_date: profile?.qualifications?.hire_date?.split('T')[0] || '',
            employment_type: profile?.qualifications?.employment_type || 'employee',
            employment_status: profile?.qualifications?.employment_status || 'active',
            qualification_status: profile?.qualifications?.qualification_status || 'incomplete',
            qualification_notes: profile?.qualifications?.qualification_notes || '',
            employee_id: profile?.qualifications?.employee_id || ''
        });

        const handleSubmit = async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
                await personnelApi.updateQualifications(userId, formData);
                setShowEditModal(false);
                loadProfile(); // Refresh
            } catch (err) {
                console.error('Error updating qualifications:', err);
                alert(err.response?.data?.message || 'Failed to update qualifications');
            } finally {
                setSaving(false);
            }
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                    <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white p-4 rounded-t-xl">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <Edit className="w-5 h-5" />
                                <h2 className="text-lg font-bold">Edit Qualifications</h2>
                            </div>
                            <button onClick={() => setShowEditModal(false)} className="hover:bg-white/20 p-1 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="p-6 space-y-4">
                        {/* Employee ID */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                            <input
                                type="text"
                                value={formData.employee_id}
                                onChange={(e) => setFormData(prev => ({ ...prev, employee_id: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="e.g., EMP-001"
                            />
                        </div>

                        {/* Department */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                            <input
                                type="text"
                                value={formData.department}
                                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="e.g., Clinical Operations"
                            />
                        </div>

                        {/* Job Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                            <input
                                type="text"
                                value={formData.job_title}
                                onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                placeholder="e.g., Clinical Research Associate"
                            />
                        </div>

                        {/* Hire Date */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Hire Date</label>
                            <input
                                type="date"
                                value={formData.hire_date}
                                onChange={(e) => setFormData(prev => ({ ...prev, hire_date: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                            />
                        </div>

                        {/* Employment Type & Status */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                                <select
                                    value={formData.employment_type}
                                    onChange={(e) => setFormData(prev => ({ ...prev, employment_type: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                >
                                    {personnelApi.employmentTypes.map(type => (
                                        <option key={type.value} value={type.value}>{type.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Employment Status</label>
                                <select
                                    value={formData.employment_status}
                                    onChange={(e) => setFormData(prev => ({ ...prev, employment_status: e.target.value }))}
                                    className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                >
                                    {personnelApi.employmentStatuses.map(status => (
                                        <option key={status.value} value={status.value}>{status.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Qualification Status */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Qualification Status</label>
                            <select
                                value={formData.qualification_status}
                                onChange={(e) => setFormData(prev => ({ ...prev, qualification_status: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                            >
                                <option value="qualified">Qualified</option>
                                <option value="pending_review">Pending Review</option>
                                <option value="incomplete">Incomplete</option>
                                <option value="expired">Expired</option>
                            </select>
                        </div>

                        {/* Qualification Notes */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                                value={formData.qualification_notes}
                                onChange={(e) => setFormData(prev => ({ ...prev, qualification_notes: e.target.value }))}
                                className="w-full border rounded-lg p-2 focus:ring-2 focus:ring-[#E91E63]"
                                rows={2}
                                placeholder="Optional notes about qualifications"
                            />
                        </div>

                        {/* Submit */}
                        <div className="flex gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => setShowEditModal(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className="flex-1 px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B] disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {saving ? (
                                    <>
                                        <RefreshCw className="w-4 h-4 animate-spin" />
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        Save Changes
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    // Help Modal Component - Content per QMS-002 v1.1
    const HelpModal = () => {
        const [activeSection, setActiveSection] = useState('overview');

        const sections = [
            { id: 'overview', label: 'Overview' },
            { id: 'qualification', label: 'Qualification File' },
            { id: 'responsibilities', label: 'Responsibilities' },
            { id: 'retention', label: 'Retention' }
        ];

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white p-4">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-2">
                                <HelpCircle className="w-6 h-6" />
                                <h2 className="text-xl font-bold">Personnel Profile Help</h2>
                            </div>
                            <button onClick={() => setShowHelpModal(false)} className="hover:bg-white/20 p-1 rounded">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <p className="text-sm text-white/80 mt-1">Per QMS-002 v1.1 — Training and Competency Management</p>
                    </div>

                    <div className="flex flex-1 overflow-hidden">
                        {/* Sidebar */}
                        <div className="w-48 bg-gray-50 border-r p-2 overflow-y-auto">
                            {sections.map(section => (
                                <button
                                    key={section.id}
                                    onClick={() => setActiveSection(section.id)}
                                    className={`w-full text-left px-3 py-2 rounded text-sm mb-1 ${
                                        activeSection === section.id
                                            ? 'bg-[#E91E63] text-white'
                                            : 'hover:bg-gray-200 text-gray-700'
                                    }`}
                                >
                                    {section.label}
                                </button>
                            ))}
                        </div>

                        {/* Content */}
                        <div className="flex-1 p-6 overflow-y-auto">
                            {activeSection === 'overview' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-gray-900">Purpose</h3>
                                    <p className="text-gray-600">
                                        This page supports QMS-002 by maintaining Personnel Qualification Files for all KLINISOL 
                                        personnel, contract CRAs, and external consultants as required for audit readiness.
                                    </p>
                                    
                                    <h4 className="font-semibold text-gray-800 mt-4">Regulatory References (per QMS-002 Section 6.0):</h4>
                                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                                        <li>ICH E6 (R3) — Good Clinical Practice, Section 2.8 Personnel Qualifications</li>
                                        <li>ISO 9001:2015 — Quality Management Systems, Clause 7.2 Competence</li>
                                        <li>FDA 21 CFR Part 11 — Electronic Records; Electronic Signatures</li>
                                    </ul>

                                    <h4 className="font-semibold text-gray-800 mt-4">Scope (per QMS-002 Section 2.0):</h4>
                                    <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
                                        <li>Core Team Members — permanent staff of KLINISOL CRO</li>
                                        <li>Contract Clinical Research Associates — CRAs engaged on contract basis</li>
                                        <li>External Consultants — QA auditors, biostatisticians, regulatory specialists</li>
                                        <li>Temporary Personnel — short-term staff for specific projects</li>
                                    </ul>
                                </div>
                            )}

                            {activeSection === 'qualification' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-gray-900">Personnel Qualification File Contents</h3>
                                    <p className="text-gray-600">Per QMS-002 Section 5.7.2, QA Manager maintains comprehensive Personnel Qualification File for each individual containing:</p>
                                    
                                    <table className="w-full text-sm border mt-2">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="text-left p-2 border">Document</th>
                                                <th className="text-left p-2 border">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr><td className="p-2 border font-medium">Current CV or resume</td><td className="p-2 border text-gray-600"></td></tr>
                                            <tr><td className="p-2 border font-medium">Education credentials</td><td className="p-2 border text-gray-600">Degrees, certifications</td></tr>
                                            <tr><td className="p-2 border font-medium">Current GCP certification</td><td className="p-2 border text-gray-600"></td></tr>
                                            <tr><td className="p-2 border font-medium">Professional licenses or certifications</td><td className="p-2 border text-gray-600">If applicable</td></tr>
                                            <tr><td className="p-2 border font-medium">All Training Records</td><td className="p-2 border text-gray-600">QMS-002-F02</td></tr>
                                            <tr><td className="p-2 border font-medium">All Competency Evaluation Forms</td><td className="p-2 border text-gray-600">QMS-002-F03</td></tr>
                                            <tr><td className="p-2 border font-medium">Annual Competency Review Summaries</td><td className="p-2 border text-gray-600"></td></tr>
                                            <tr><td className="p-2 border font-medium">Delegation of Authority documentation</td><td className="p-2 border text-gray-600"></td></tr>
                                            <tr><td className="p-2 border font-medium">Contract or employment agreement</td><td className="p-2 border text-gray-600">As applicable</td></tr>
                                            <tr><td className="p-2 border font-medium">Conflict of interest declaration</td><td className="p-2 border text-gray-600"></td></tr>
                                            <tr><td className="p-2 border font-medium">Confidentiality agreement</td><td className="p-2 border text-gray-600"></td></tr>
                                        </tbody>
                                    </table>

                                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                                        <p className="font-semibold text-blue-800">Audit Readiness (Section 5.7.4)</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            Training records must be audit-ready and retrievable within 24 hours of request.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {activeSection === 'responsibilities' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-gray-900">Responsibilities</h3>
                                    <p className="text-gray-600">Per QMS-002 Section 3.0:</p>
                                    
                                    <table className="w-full text-sm border mt-2">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="text-left p-2 border" style={{width: '30%'}}>Role</th>
                                                <th className="text-left p-2 border">Responsibilities</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="p-2 border font-medium align-top">Quality Assurance Manager</td>
                                                <td className="p-2 border text-gray-600">
                                                    <ul className="list-disc list-inside space-y-1">
                                                        <li>Serves as Training Coordinator</li>
                                                        <li>Maintains master training records and personnel qualification files</li>
                                                        <li>Tracks training completion</li>
                                                        <li>Ensures records are audit-ready</li>
                                                    </ul>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 border font-medium align-top">Chief Quality Officer</td>
                                                <td className="p-2 border text-gray-600">
                                                    <ul className="list-disc list-inside space-y-1">
                                                        <li>Approves Training Matrices</li>
                                                        <li>Approves qualification of contract CRAs and external consultants</li>
                                                    </ul>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 border font-medium align-top">Department Heads</td>
                                                <td className="p-2 border text-gray-600">
                                                    <ul className="list-disc list-inside space-y-1">
                                                        <li>Identify training requirements</li>
                                                        <li>Conduct competency assessments</li>
                                                        <li>Review annual competency assessment results</li>
                                                    </ul>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 border font-medium align-top">All Personnel</td>
                                                <td className="p-2 border text-gray-600">
                                                    <ul className="list-disc list-inside space-y-1">
                                                        <li>Complete all required training before performing GCP activities</li>
                                                        <li>Maintain current GCP certification (renewal every 2-3 years)</li>
                                                        <li>Achieve minimum passing score (≥80%) on assessments</li>
                                                    </ul>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                                        <p className="font-semibold text-blue-800">Access Control (Section 5.7.1)</p>
                                        <p className="text-sm text-blue-700 mt-1">
                                            Access to training records restricted to QA Manager, Department Heads, and Chief Quality Officer.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {activeSection === 'retention' && (
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-gray-900">Training Record Retention</h3>
                                    <p className="text-gray-600">Per QMS-002 Section 5.7.3:</p>
                                    
                                    <table className="w-full text-sm border mt-2">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="text-left p-2 border">Record Type</th>
                                                <th className="text-left p-2 border">Retention Period</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="p-2 border font-medium">Core Team Personnel Files</td>
                                                <td className="p-2 border text-gray-600">7 years after employment termination</td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 border font-medium">Contract CRA Personnel Files</td>
                                                <td className="p-2 border text-gray-600">7 years after contract completion</td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 border font-medium">External Consultant Files</td>
                                                <td className="p-2 border text-gray-600">7 years after engagement completion</td>
                                            </tr>
                                            <tr>
                                                <td className="p-2 border font-medium">Study-Specific Training</td>
                                                <td className="p-2 border text-gray-600">Per study retention requirements (typically 7 years post-study completion)</td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    <p className="text-sm text-gray-500 mt-4">
                                        These retention periods align with KLINISOL's 7-year retention policy per QMS-001 and exceed 
                                        FDA minimum requirements for IND studies (2 years post-IND closure).
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
                        <p className="text-xs text-gray-500">
                            Reference: QMS-002 v1.1 | Training and Competency Management
                        </p>
                        <button
                            onClick={() => setShowHelpModal(false)}
                            className="px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B]"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Status badge helper
    const getStatusBadge = (status) => {
        const badges = {
            'approved': <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Approved</span>,
            'pending_review': <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending Review</span>,
            'rejected': <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Rejected</span>,
            'expired': <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Expired</span>
        };
        return badges[status] || badges['pending_review'];
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <RefreshCw className="w-8 h-8 animate-spin text-[#475569] mx-auto mb-4" />
                    <p className="text-gray-600">Loading profile...</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-600 mb-4">{error}</p>
                    <button
                        onClick={() => navigate('/personnel')}
                        className="px-4 py-2 bg-[#475569] text-white rounded-lg hover:bg-[#334155]"
                    >
                        Back to Directory
                    </button>
                </div>
            </div>
        );
    }

    const canUpload = accessLevel === 'admin' || String(currentUser?.id) === String(userId);
    const canApprove = accessLevel === 'admin';

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Upload Modal */}
            {showUploadModal && <UploadModal />}
            {showCredentialModal && <CredentialModal />}
            {showEditModal && <EditQualificationsModal />}
            {showHelpModal && <HelpModal />}

            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex justify-between items-start">
                        <div className="flex items-center gap-4">
                            <button 
                                onClick={() => navigate('/personnel')}
                                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5" />
                            </button>
                            <div className="flex items-center gap-4">
                                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                                    <User className="w-8 h-8" />
                                </div>
                                <div>
                                    <h1 className="text-2xl font-bold">
                                        {profile?.user?.first_name} {profile?.user?.last_name}
                                    </h1>
                                    <p className="text-sm opacity-90">{profile?.qualifications?.job_title || 'Staff Member'}</p>
                                    {profile?.functionalRole && (
                                        <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 rounded text-xs">
                                            {profile.functionalRole.role_code}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {canUpload && (
                            <button
                                onClick={() => setShowUploadModal(true)}
                                className="px-4 py-2 bg-white text-[#E91E63] hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2 font-medium"
                            >
                                <Upload className="w-4 h-4" />
                                Upload Document
                            </button>
                        )}
                        {canApprove && (
                            <button
                                onClick={() => setShowEditModal(true)}
                                className="px-4 py-2 bg-white/20 text-white hover:bg-white/30 rounded-lg transition-colors flex items-center gap-2 font-medium border border-white/30"
                            >
                                <Edit className="w-4 h-4" />
                                Edit Profile
                            </button>
                        )}
                        <button
                            onClick={() => setShowHelpModal(true)}
                            className="p-2 bg-white/20 text-white hover:bg-white/30 rounded-lg transition-colors border border-white/30"
                            title="Help"
                        >
                            <HelpCircle className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Profile Info Cards */}
            <div className="max-w-7xl mx-auto px-4 py-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-xs text-gray-500">Email</p>
                                <p className="text-sm font-medium">{profile?.user?.email}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center gap-3">
                            <Briefcase className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-xs text-gray-500">Department</p>
                                <p className="text-sm font-medium">{profile?.qualifications?.department || '-'}</p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-xs text-gray-500">Hire Date</p>
                                <p className="text-sm font-medium">
                                    {profile?.qualifications?.hire_date 
                                        ? new Date(profile.qualifications.hire_date).toLocaleDateString()
                                        : '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg shadow p-4">
                        <div className="flex items-center gap-3">
                            <Shield className="w-5 h-5 text-gray-400" />
                            <div>
                                <p className="text-xs text-gray-500">Qualification Status</p>
                                <p className="text-sm font-medium capitalize">
                                    {profile?.qualifications?.qualification_status || 'Incomplete'}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-lg shadow mb-6">
                    <div className="border-b">
                        <nav className="flex">
                            <button
                                onClick={() => setActiveTab('documents')}
                                className={`px-6 py-3 font-medium text-sm border-b-2 ${
                                    activeTab === 'documents'
                                        ? 'border-[#E91E63] text-[#E91E63]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <FileText className="w-4 h-4 inline mr-2" />
                                Documents ({profile?.documents?.length || 0})
                            </button>
                            <button
                                onClick={() => setActiveTab('credentials')}
                                className={`px-6 py-3 font-medium text-sm border-b-2 ${
                                    activeTab === 'credentials'
                                        ? 'border-[#E91E63] text-[#E91E63]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <Award className="w-4 h-4 inline mr-2" />
                                Credentials ({profile?.credentials?.length || 0})
                            </button>
                        </nav>
                    </div>

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div className="p-4">
                            {profile?.documents?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                                            <tr>
                                                <th className="px-4 py-3">Type</th>
                                                <th className="px-4 py-3">Title</th>
                                                <th className="px-4 py-3">Version</th>
                                                <th className="px-4 py-3">Date</th>
                                                <th className="px-4 py-3">Status</th>
                                                <th className="px-4 py-3">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {profile.documents.map(doc => (
                                                <tr key={doc.id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3">
                                                        <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                                                            {doc.document_type.replace(/_/g, ' ')}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 font-medium">{doc.document_title || doc.file_name}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">v{doc.version}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-500">
                                                        {doc.document_date ? new Date(doc.document_date).toLocaleDateString() : '-'}
                                                    </td>
                                                    <td className="px-4 py-3">{getStatusBadge(doc.approval_status)}</td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex gap-2">
                                                            <button
                                                                onClick={() => handleDownload(doc.id, doc.file_name)}
                                                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                title="Download"
                                                            >
                                                                <Download className="w-4 h-4" />
                                                            </button>
                                                            {canApprove && doc.approval_status === 'pending_review' && (
                                                                <>
                                                                    <button
                                                                        onClick={() => handleApproval(doc.id, 'approved')}
                                                                        className="p-1 text-green-600 hover:bg-green-50 rounded"
                                                                        title="Approve"
                                                                    >
                                                                        <CheckCircle className="w-4 h-4" />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleApproval(doc.id, 'rejected')}
                                                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                                        title="Reject"
                                                                    >
                                                                        <XCircle className="w-4 h-4" />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                    <p>No documents uploaded</p>
                                    {canUpload && (
                                        <button
                                            onClick={() => setShowUploadModal(true)}
                                            className="mt-4 px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B]"
                                        >
                                            Upload First Document
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Credentials Tab */}
                    {activeTab === 'credentials' && (
                        <div className="p-4">
                            {/* Add Credential Button */}
                            {canUpload && (
                                <div className="mb-4 flex justify-end">
                                    <button
                                        onClick={() => setShowCredentialModal(true)}
                                        className="px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B] flex items-center gap-2"
                                    >
                                        <Plus className="w-4 h-4" />
                                        Add Credential
                                    </button>
                                </div>
                            )}
                            {profile?.credentials?.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase">
                                            <tr>
                                                <th className="px-4 py-3">Type</th>
                                                <th className="px-4 py-3">Name</th>
                                                <th className="px-4 py-3">Issuing Body</th>
                                                <th className="px-4 py-3">Issue Date</th>
                                                <th className="px-4 py-3">Expiration</th>
                                                <th className="px-4 py-3">Verification</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {profile.credentials.map(cred => {
                                                const isExpired = cred.expiration_date && new Date(cred.expiration_date) < new Date();
                                                const isExpiringSoon = cred.expiration_date && 
                                                    new Date(cred.expiration_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                                                
                                                return (
                                                    <tr key={cred.id} className="hover:bg-gray-50">
                                                        <td className="px-4 py-3">
                                                            <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                                                                {cred.credential_type.replace(/_/g, ' ')}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3 font-medium">{cred.credential_name}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">{cred.issuing_body}</td>
                                                        <td className="px-4 py-3 text-sm text-gray-500">
                                                            {new Date(cred.issue_date).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {cred.expiration_date ? (
                                                                <span className={`flex items-center gap-1 text-sm ${
                                                                    isExpired ? 'text-red-600' : 
                                                                    isExpiringSoon ? 'text-yellow-600' : 'text-gray-500'
                                                                }`}>
                                                                    {isExpired && <AlertTriangle className="w-4 h-4" />}
                                                                    {isExpiringSoon && !isExpired && <Clock className="w-4 h-4" />}
                                                                    {new Date(cred.expiration_date).toLocaleDateString()}
                                                                </span>
                                                            ) : (
                                                                <span className="text-sm text-gray-400">No expiration</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {cred.verification_status === 'verified' ? (
                                                                <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">Verified</span>
                                                            ) : (
                                                                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs">Pending</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-12 text-gray-500">
                                    <Award className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                    <p>No credentials recorded</p>
                                    {canUpload && (
                                        <button
                                            onClick={() => setShowCredentialModal(true)}
                                            className="mt-4 px-4 py-2 bg-[#E91E63] text-white rounded-lg hover:bg-[#C2185B]"
                                        >
                                            Add First Credential
                                        </button>
                                    )}
                                </div>
                            )}}
                        </div>
                    )}
                </div>

                {/* Training Summary */}
                {profile?.trainingSummary && (
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="font-semibold text-gray-900 mb-3">Training Compliance</h3>
                        <div className="grid grid-cols-3 gap-4">
                            <div className={`text-center p-3 rounded ${
                                profile.trainingSummary.completed < profile.trainingSummary.total_assigned 
                                    ? 'bg-yellow-50' : 'bg-green-50'
                            }`}>
                                <p className={`text-2xl font-bold ${
                                    profile.trainingSummary.completed < profile.trainingSummary.total_assigned 
                                        ? 'text-yellow-600' : 'text-green-600'
                                }`}>
                                    {profile.trainingSummary.completed || 0} of {profile.trainingSummary.total_assigned || 0}
                                </p>
                                <p className="text-xs text-gray-500">Required Training</p>
                            </div>
                            <div className="text-center p-3 bg-green-50 rounded">
                                <p className="text-2xl font-bold text-green-600">{profile.trainingSummary.certified || 0}</p>
                                <p className="text-xs text-gray-500">Certifications Valid</p>
                            </div>
                            <div className={`text-center p-3 rounded ${
                                (profile.trainingSummary.needs_attention || 0) > 0 
                                    ? 'bg-red-50' : 'bg-gray-50'
                            }`}>
                                <p className={`text-2xl font-bold ${
                                    (profile.trainingSummary.needs_attention || 0) > 0 
                                        ? 'text-red-600' : 'text-gray-400'
                                }`}>
                                    {profile.trainingSummary.needs_attention || 0}
                                </p>
                                <p className="text-xs text-gray-500">Needs Attention</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PersonnelProfile;
